<script setup lang="ts">
import { useWorkshops } from '~/composables/useWorkshops'

const props = defineProps<{
    workshopId: string
    workshopTitle: string
    workshopPrice: number
}>()

const emit = defineEmits<{
    cancel: []
}>()

const { register } = useWorkshops()

const form = ref({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    website: '' // Honeypot — never shown to user
})

const formLoadedAt = ref(0)
const isSubmitting = ref(false)
const errorMessage = ref('')

onMounted(() => {
    formLoadedAt.value = Date.now()
})

const submitForm = async () => {
    isSubmitting.value = true
    errorMessage.value = ''

    try {
        const result = await register(props.workshopId, {
            firstName: form.value.firstName,
            lastName: form.value.lastName,
            email: form.value.email,
            phone: form.value.phone || undefined,
            website: form.value.website || undefined,
            formLoadedAt: formLoadedAt.value
        })

        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl
    } catch (e: any) {
        const errorData = e.response?._data
        errorMessage.value = errorData?.message || 'Une erreur est survenue. Veuillez réessayer.'
    } finally {
        isSubmitting.value = false
    }
}
</script>

<template>
    <div class="mt-4 p-6 rounded-xl border border-oxo-border bg-background">
        <h4 class="font-display font-bold text-lg text-foreground mb-4">
            Inscription – {{ workshopTitle }}
        </h4>
        <p class="text-foreground-dim text-sm mb-4">
            Montant : <strong>{{ workshopPrice.toFixed(2) }} €</strong> — vous serez redirigé(e) vers notre page de paiement sécurisé.
        </p>

        <!-- Error -->
        <div v-if="errorMessage" class="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
            <i class="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
            {{ errorMessage }}
        </div>

        <form @submit.prevent="submitForm" class="space-y-4">
            <input v-model="form.website" type="text" name="website" autocomplete="off" tabindex="-1" aria-hidden="true" class="hidden" />

            <!-- Vos informations -->
            <fieldset>
                <legend class="text-sm font-bold text-foreground-dim uppercase tracking-wider mb-3">Vos informations</legend>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label class="block text-sm font-bold text-foreground-dim mb-1">Prénom *</label>
                        <input v-model="form.firstName" type="text" required
                            class="w-full px-4 py-2 rounded-lg bg-oxo-card-bg border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
                            placeholder="Jean" />
                    </div>
                    <div>
                        <label class="block text-sm font-bold text-foreground-dim mb-1">Nom *</label>
                        <input v-model="form.lastName" type="text" required
                            class="w-full px-4 py-2 rounded-lg bg-oxo-card-bg border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
                            placeholder="Dupont" />
                    </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div>
                        <label class="block text-sm font-bold text-foreground-dim mb-1">Email *</label>
                        <input v-model="form.email" type="email" required
                            class="w-full px-4 py-2 rounded-lg bg-oxo-card-bg border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
                            placeholder="jean.dupont@example.com" />
                    </div>
                    <div>
                        <label class="block text-sm font-bold text-foreground-dim mb-1">Téléphone</label>
                        <input v-model="form.phone" type="tel"
                            class="w-full px-4 py-2 rounded-lg bg-oxo-card-bg border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
                            placeholder="06 12 34 56 78" />
                    </div>
                </div>
            </fieldset>

            <p class="text-xs text-foreground-dim/60">
                Vos données sont utilisées uniquement pour la gestion de l'inscription et le paiement.
                En cliquant sur "Procéder au paiement", vous serez redirigé(e) vers la page de paiement sécurisée Stripe.
            </p>

            <div class="flex gap-3">
                <button type="button" @click="emit('cancel')"
                    class="btn-secondary flex-1">
                    Annuler
                </button>
                <button type="submit" :disabled="isSubmitting"
                    class="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    <i v-if="isSubmitting" class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
                    <span v-else>Procéder au paiement</span>
                </button>
            </div>
        </form>
    </div>
</template>
